import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GridEditModal } from "../editSearch/gridEditModal.tsx";
import { Button } from "../ui/button.tsx";
import { useToast } from "../../hooks/useToast.ts";
import { useAuth } from "../../auth/authContext.tsx";
import { getDbFeatureFlags } from "../../util/dbFeatureFlags.ts";
import {
    getEditTransaction,
    saveEditTransaction,
    getProductKioskPrice,
    editCartGetPromotionCode,
    type TransactionChangeHead,
    type TransactionChangeRow,
    type TransactionChangePayment,
    type TransactionRentalInfo,
    type UpdateTransactionContainer,
    type TransactionSettlement,
} from "../../api/specialized.ts";
import { computeRentalRecalc } from "../../util/rentalRecalc.ts";
import { ErrorType } from "../../types/returnObject.ts";
import { defaultRefundOrder, refundSources, subsidyAdjustments, type RefundSource, type SubsidyAdjustment } from "./refundAllocation.ts";
import { RefundSourcesPanel, type RefundPlanEntry } from "./refundSourcesPanel.tsx";
import { settleIntent, settlement, type SettleAction } from "./cartSettlement.ts";
import styles from "./editCartModal.module.scss";

// ── Types & helpers ────────────────────────────────────────

type RowCategory = "purchase" | "rental" | "package" | "promotion" | "other";

interface SubItem {
    productID: number;
    unitPrice: number;
    unitTax: number;
    quantity: number;
    rowIdx?: number; // index in head.Rows if existing
}

interface PackageGroup {
    groupID: number;
    packageID: number;
    packageType: string; // D=Package, S=Subsidy, R=ExternalReduction, T=ExternalTender
    unitPrice: number;
    unitTax: number;
    quantity: number;
    items: SubItem[];
    parentRowIdx?: number;
    isNew: boolean;
}

interface PromotionData {
    code: string;
    promotionID: number;
    promotionCodeID: number;
    initPrice: number;
    initTax: number;
    latePrice: number;
    lateTax: number;
    items: SubItem[];
    parentRowIdx?: number;
    isNew: boolean;
}

function classifyRow(row: TransactionChangeRow): RowCategory {
    switch (row.RowType) {
        case 2: case 3: return "purchase";
        case 1: case 45: return "rental";
        case 23: case 36: case 40: case 42: return "package";
        case 24: case 37: case 41: case 43: case 44: return "other"; // sub-items handled separately
        case 6: return "promotion";
        default: return "other";
    }
}

function isSubItemType(t: number): boolean {
    return [24, 37, 41, 43, 44].includes(t);
}

function fmtAmt(n: number | undefined): string {
    return (n ?? 0).toFixed(2);
}

/** Trim a server ISO timestamp ("2026-06-24T18:07:00.0000000") to "YYYY-MM-DD HH:mm". */
function fmtDateShort(d: string | undefined): string {
    if (!d) return "";
    const m = /^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})/.exec(String(d));
    return m ? `${m[1]} ${m[2]}` : String(d);
}

let nextNewId = -1;

/* Legacy "no due date" sentinel used by WebAdmin2 for non-rental rows
 * (popupEditCart2.ts:650). */
const ZERO_DATE = "1999-01-01T00:00:00";

/** Server-local "now" as an unzoned ISO string ("YYYY-MM-DDTHH:mm:ss").
 *  Mirrors WebAdmin2's ConvertDateServer (utility/datetime.ts:69): shift the
 *  client clock by (clientTZ + ServerTimezoneOffset) minutes. New rows get
 *  their DeliverDate/ReturnDate stamped with this, matching what the SQL
 *  DATETIME columns expect (server-local, no offset suffix). */
function serverNowIso(serverTzOffsetMin: number): string {
    const now = new Date();
    const t = new Date(now.getTime() + (now.getTimezoneOffset() + serverTzOffsetMin) * 60000);
    const p = (n: number) => String(n).padStart(2, "0");
    return `${t.getFullYear()}-${p(t.getMonth() + 1)}-${p(t.getDate())}T${p(t.getHours())}:${p(t.getMinutes())}:${p(t.getSeconds())}`;
}

/** Head totals written to Customer_Transaction_Head (iPrice/iTax/iLate*) by
 *  the server, and used as the refund/charge target (Transactions.vb:495).
 *  Must mirror WebAdmin2's WorkingDataSummary.Calc (popupEditCart2.ts:396-500):
 *   - purchases (2/3) and package/external-reduction PARENTS (23/40): qty × unit
 *   - rentals/reserves (1/45): recalc final — rental base + buy when sold to
 *     Initial, computed late fee to Late
 *   - promotion (6): InitialAmount/Tax only — legacy explicitly comments OUT
 *     the promo late contribution (popupEditCart2.ts:483) — quirk parity
 *   - subsidy/external-tender parents (36/42): EXCLUDED — legacy books them
 *     in a separate Subsidy container, never in head totals; the server's
 *     refund calc offsets them on the payments side instead
 *   - sub-item children (24/37/41/43/44), refund markers (16) and anything
 *     else: excluded — informational, not cart money */
function computeHeadTotals(rows: TransactionChangeRow[]) {
    let initAmt = 0, initTax = 0, lateAmt = 0, lateTax = 0;
    for (const r of rows) {
        const q = r.Quantity ?? 1;
        switch (r.RowType) {
            case 2: case 3:
            case 23: case 40:
                initAmt += (r.InitialAmount ?? 0) * q;
                initTax += (r.InitialTax ?? 0) * q;
                break;
            case 1: case 45: {
                const rc = computeRentalRecalc(r);
                initAmt += rc.rentalPrice + rc.buyPrice;
                initTax += rc.rentalTax + rc.buyTax;
                lateAmt += rc.latePrice;
                lateTax += rc.lateTax;
                break;
            }
            case 6:
                initAmt += r.InitialAmount ?? 0;
                initTax += r.InitialTax ?? 0;
                break;
        }
    }
    return { initAmt, initTax, lateAmt, lateTax };
}

// ── Shared input components ────────────────────────────────

function AmtInput({ value, onChange, disabled, negative }: {
    value: number; onChange: (v: number) => void; disabled?: boolean; negative?: boolean;
}) {
    return (
        <input
            className={styles.amtInput}
            type="number"
            step="0.01"
            value={value}
            onChange={e => {
                let v = parseFloat(e.target.value) || 0;
                if (negative && v > 0) v = -v;
                onChange(v);
            }}
            disabled={disabled}
        />
    );
}

// ── Purchases ──────────────────────────────────────────────

function SimpleRowsSection({
    title, category, rows, indices, productNames, productList, kioskID,
    onRowChange, onRemoveRow, onAddRow,
}: {
    title: string;
    category: "purchase";
    rows: TransactionChangeRow[];
    indices: number[];
    productNames: Record<number, string>;
    productList: { id: number; name: string }[];
    kioskID: number;
    onRowChange: (idx: number, field: string, value: unknown) => void;
    onRemoveRow: (idx: number) => void;
    onAddRow: () => void;
}) {
    const { toast } = useToast();
    const canAdd = category === "purchase";

    const handleProductChange = async (gIdx: number, pid: number) => {
        onRowChange(gIdx, "Product", pid);
        if (pid > 0 && kioskID > 0) {
            try {
                // Server returns Dictionary<int, PriceTypeResult> as a JSON object
                // keyed by stringified PriceType: {"1": {Value, Weight}, "2": {...}}.
                // Each entry wraps the resolved price in `.Value` (nullable — omitted
                // when there's no price for that type) alongside the override's
                // `.Weight`, not a plain number — indexing straight into the entry
                // and checking `typeof === "number"` is always false against an
                // object, so the price-fill block silently never ran.
                const r = await getProductKioskPrice(pid, kioskID);
                if (!r.bError && r.oData && typeof r.oData === "object") {
                    const price = r.oData["1"]?.Value;
                    const tax = r.oData["2"]?.Value;
                    if (typeof price === "number") onRowChange(gIdx, "InitialAmount", price);
                    if (typeof tax === "number")   onRowChange(gIdx, "InitialTax", tax);
                }
            } catch { toast("Failed to fetch price", "error"); }
        }
    };

    if (rows.length === 0 && !canAdd) return null;

    const sectionTotal = rows.reduce((s, r) => {
        const q = r.Quantity ?? 1;
        return s + ((r.InitialAmount ?? 0) + (r.InitialTax ?? 0) + (r.LateAmount ?? 0) + (r.LateTax ?? 0)) * q;
    }, 0);

    return (
        <div className={styles.section}>
            <div className={styles.sectionHeader}>
                <span className={styles.sectionTitle}>{title}</span>
                {rows.length > 0 && <span className={styles.badge}>{fmtAmt(sectionTotal)}</span>}
                {canAdd && <button className={styles.addBtn} onClick={onAddRow}>+ Add</button>}
            </div>
            {rows.length > 0 && (
                <table className={styles.cartTable}>
                    <thead>
                        <tr>
                            <th>Product</th>
                            <th className={styles.thR}>Price</th>
                            <th className={styles.thR}>Tax</th>
                            <th className={styles.thR}>Qty</th>
                            <th>Status</th>
                            <th className={styles.thAction}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, li) => {
                            const gi = indices[li]!;
                            const isNew = (row.RowID ?? 0) <= 0;
                            const pName = productNames[row.Product];
                            return (
                                <tr key={gi} className={isNew ? styles.newRow : undefined}>
                                    <td className={styles.tdProduct}>
                                        {isNew ? (
                                            <SearchableSelect
                                                items={productList}
                                                value={row.Product}
                                                onChange={pid => handleProductChange(gi, pid)}
                                                placeholder="Search product..."
                                            />
                                        ) : (
                                            <span className={styles.pLabel}>
                                                <span className={styles.pId}>{row.Product}</span>
                                                {pName && <span className={styles.pName}>{pName}</span>}
                                            </span>
                                        )}
                                    </td>
                                    <td className={styles.tdR}><AmtInput value={row.InitialAmount ?? 0} onChange={v => onRowChange(gi, "InitialAmount", v)} /></td>
                                    <td className={styles.tdR}><AmtInput value={row.InitialTax ?? 0} onChange={v => onRowChange(gi, "InitialTax", v)} /></td>
                                    <td className={styles.tdR}>
                                        <input className={styles.qtyInput} type="number" min="0" value={row.Quantity ?? 1} onChange={e => onRowChange(gi, "Quantity", parseInt(e.target.value) || 0)} />
                                    </td>
                                    <td>{row.Status}</td>
                                    <td className={styles.tdAction}>{isNew && <button className={styles.removeBtn} onClick={() => onRemoveRow(gi)}>&times;</button>}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            )}
        </div>
    );
}

// ── Rentals & Reserves ─────────────────────────────────────
/* WebAdmin2 renders each rental/reserve item as a breakdown card: a
 * Rent | Late Period | Buy grid (Price & Tax rows), the computed Late
 * Periods count, and the resulting Final. Only the Rent column is
 * editable (InitialAmount / InitialTax); Late Period and Buy mirror the
 * row's RentalInfo and are read-only. For a rental (RowType 1) the late
 * period equals the rent, so the Late Period column is greyed/muted
 * (reference only); for a reserve (45) it's a separate price/rate, shown
 * normally — and its tax is a percentage rate, not a flat amount.
 *
 * When RentalInfo is absent (rc.applicable === false, non-rental DBs) we
 * fall back to a flat editable row (Price/Tax + editable Late Price/Tax)
 * matching the legacy manual-input behaviour. */

function RentalRow({ row, gi, productNames, productList, onRowChange, onRentalInfoChange }: {
    row: TransactionChangeRow;
    gi: number;
    productNames: Record<number, string>;
    productList: { id: number; name: string }[];
    onRowChange: (idx: number, field: string, value: unknown) => void;
    onRentalInfoChange: (idx: number, field: string, value: number) => void;
}) {
    const rc = computeRentalRecalc(row);
    const isNew = (row.RowID ?? 0) <= 0;
    const pName = productNames[row.Product];
    const ri = row.RentalInfo;
    const isRentalType = row.RowType === 1; // 1 = rental, 45 = reserve

    const header = (
        <div className={styles.rentalHeader}>
            {isNew ? (
                <SearchableSelect
                    items={productList}
                    value={row.Product}
                    onChange={pid => onRowChange(gi, "Product", pid)}
                    placeholder="Search product..."
                />
            ) : (
                <span className={styles.pLabel}>
                    <span className={styles.typeTag}>{rc.isReserve ? "Reserve" : "Rental"}</span>
                    <span className={styles.pId}>#{row.Product}</span>
                    {pName && <span className={styles.pName}>{pName}</span>}
                </span>
            )}
            {row.Status && <span className={styles.rentalStatus}>{row.Status}</span>}
        </div>
    );

    // Non-applicable fallback: flat editable row (Price/Tax + editable Late).
    if (!rc.applicable) {
        return (
            <div className={`${styles.rentalCard}${isNew ? ` ${styles.newRentalCard}` : ""}`}>
                {header}
                <div className={styles.rentalFlat}>
                    <label className={styles.fieldLabel}>Price<AmtInput value={row.InitialAmount ?? 0} onChange={v => onRowChange(gi, "InitialAmount", v)} /></label>
                    <label className={styles.fieldLabel}>Tax<AmtInput value={row.InitialTax ?? 0} onChange={v => onRowChange(gi, "InitialTax", v)} /></label>
                    <label className={styles.fieldLabel}>Late Price<AmtInput value={row.LateAmount ?? 0} onChange={v => onRowChange(gi, "LateAmount", v)} /></label>
                    <label className={styles.fieldLabel}>Late Tax<AmtInput value={row.LateTax ?? 0} onChange={v => onRowChange(gi, "LateTax", v)} /></label>
                </div>
            </div>
        );
    }

    // Late Period tax: reserve (45) is a rate (%); rental (1) is a flat amount.
    const lateTaxDisplay = isRentalType ? fmtAmt(ri?.LateTaxRate) : `${ri?.LateTaxRate ?? 0}%`;
    // Rental's late period equals the rent (same data) — show muted, reference only.
    const lateMuted = isRentalType ? ` ${styles.rentalColMuted}` : "";

    return (
        <div className={`${styles.rentalCard} ${styles.rentalCardRow}${isNew ? ` ${styles.newRentalCard}` : ""}`}>
            <div className={styles.rentalInfo}>
                {header}
                {(row.DueByDate || row.ReturnDate) && (
                    <div className={styles.rentalDates}>
                        {row.DueByDate ? <span>Due: {fmtDateShort(row.DueByDate)}</span> : null}
                        {row.ReturnDate ? <span>Return: {fmtDateShort(row.ReturnDate)}</span> : null}
                    </div>
                )}
                <div className={styles.rentalFooter}>
                    <span className={styles.rentalFootItem}>Late Periods: <strong>{fmtAmt(rc.latePeriods)}</strong></span>
                    <span className={styles.rentalFootItem}>
                        Final: <strong>{fmtAmt(rc.finalPrice)}</strong> (+{fmtAmt(rc.finalTax)} tax)
                    </span>
                    {rc.sold && <span className={styles.soldTag} title="Rental converted to a sale">sold</span>}
                </div>
            </div>
            <div className={styles.rentalGrid}>
                <div className={styles.rentalGridCorner}></div>
                <div className={styles.rentalColHead}>Rent</div>
                <div className={`${styles.rentalColHead}${lateMuted}`}>Late Period</div>
                <div className={styles.rentalColHead}>Buy</div>

                <div className={styles.rentalRowLabel}>Price</div>
                <div className={styles.rentalCellEdit}><AmtInput value={row.InitialAmount ?? 0} onChange={v => onRowChange(gi, "InitialAmount", v)} /><span className={styles.cellSuffix} aria-hidden="true" /></div>
                {/* Late Period: editable for reserve (own price); rental mirrors the rent (muted, read-only). */}
                {isRentalType
                    ? <div className={`${styles.rentalCell}${lateMuted}`}>{fmtAmt(ri?.LatePeriodPrice)}</div>
                    : <div className={styles.rentalCellEdit}><AmtInput value={ri?.LatePeriodPrice ?? 0} onChange={v => onRentalInfoChange(gi, "LatePeriodPrice", v)} /><span className={styles.cellSuffix} aria-hidden="true" /></div>}
                {/* Buy / Max charge: editable for both. */}
                <div className={styles.rentalCellEdit}><AmtInput value={ri?.MaxPrice ?? 0} onChange={v => onRentalInfoChange(gi, "MaxPrice", v)} /><span className={styles.cellSuffix} aria-hidden="true" /></div>

                <div className={styles.rentalRowLabel}>Tax</div>
                <div className={styles.rentalCellEdit}><AmtInput value={row.InitialTax ?? 0} onChange={v => onRowChange(gi, "InitialTax", v)} /><span className={styles.cellSuffix} aria-hidden="true" /></div>
                {isRentalType
                    ? <div className={`${styles.rentalCell}${lateMuted}`}>{lateTaxDisplay}</div>
                    : <div className={styles.rentalCellEdit}><AmtInput value={ri?.LateTaxRate ?? 0} onChange={v => onRentalInfoChange(gi, "LateTaxRate", v)} /><span className={styles.cellSuffix}>%</span></div>}
                <div className={styles.rentalCellEdit}><AmtInput value={ri?.MaxTax ?? 0} onChange={v => onRentalInfoChange(gi, "MaxTax", v)} /><span className={styles.cellSuffix} aria-hidden="true" /></div>
            </div>
        </div>
    );
}

function RentalsSection({
    title, rows, indices, productNames, productList, onRowChange, onRentalInfoChange,
}: {
    title: string;
    rows: TransactionChangeRow[];
    indices: number[];
    productNames: Record<number, string>;
    productList: { id: number; name: string }[];
    onRowChange: (idx: number, field: string, value: unknown) => void;
    onRentalInfoChange: (idx: number, field: string, value: number) => void;
}) {
    if (rows.length === 0) return null;

    // Rental/reserve rows contribute their computed final (incl. buy when
    // sold). computeRentalRecalc handles the non-applicable case as a
    // passthrough of Initial+Late, so we can call it uniformly.
    const sectionTotal = rows.reduce((s, r) => {
        const rc = computeRentalRecalc(r);
        return s + rc.finalPrice + rc.finalTax;
    }, 0);

    return (
        <div className={styles.section}>
            <div className={styles.sectionHeader}>
                <span className={styles.sectionTitle}>{title}</span>
                <span className={styles.badge}>{fmtAmt(sectionTotal)}</span>
            </div>
            <div className={styles.rentalList}>
                {rows.map((row, li) => (
                    <RentalRow
                        key={indices[li]!}
                        row={row}
                        gi={indices[li]!}
                        productNames={productNames}
                        productList={productList}
                        onRowChange={onRowChange}
                        onRentalInfoChange={onRentalInfoChange}
                    />
                ))}
            </div>
        </div>
    );
}

// ── Sub-items table (shared by packages & promotions) ──────

function SubItemsTable({ items, products, onChange, onAdd, onRemove }: {
    items: SubItem[];
    products: { id: number; name: string }[];
    onChange: (idx: number, field: string, value: number) => void;
    onAdd: () => void;
    onRemove: (idx: number) => void;
}) {
    return (
        <div className={styles.subItems}>
            <div className={styles.subItemsHeader}>
                <span className={styles.subItemsTitle}>Line Items</span>
                <button className={styles.addBtn} onClick={onAdd}>+ Add Item</button>
            </div>
            {items.length > 0 && (
                <table className={styles.subTable}>
                    <thead>
                        <tr>
                            <th>Product</th>
                            <th className={styles.thR}>Price</th>
                            <th className={styles.thR}>Tax</th>
                            <th className={styles.thR}>Qty</th>
                            <th className={styles.thAction}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((it, i) => (
                            <tr key={i}>
                                <td>
                                    <SearchableSelect
                                        items={products}
                                        value={it.productID}
                                        onChange={v => onChange(i, "productID", v)}
                                        placeholder="Search product..."
                                    />
                                </td>
                                <td className={styles.tdR}><AmtInput value={it.unitPrice} onChange={v => onChange(i, "unitPrice", v)} /></td>
                                <td className={styles.tdR}><AmtInput value={it.unitTax} onChange={v => onChange(i, "unitTax", v)} /></td>
                                <td className={styles.tdR}><input className={styles.qtyInput} type="number" min="0" value={it.quantity} onChange={e => onChange(i, "quantity", parseInt(e.target.value) || 0)} /></td>
                                <td className={styles.tdAction}><button className={styles.removeBtn} onClick={() => onRemove(i)}>&times;</button></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
}

// ── Searchable select ──────────────────────────────────────

function SearchableSelect({ items, value, onChange, disabled, placeholder }: {
    items: { id: number; name: string }[];
    value: number;
    onChange: (id: number) => void;
    disabled?: boolean;
    placeholder?: string;
}) {
    const [search, setSearch] = useState("");
    const [open, setOpen] = useState(false);
    const wrapRef = useRef<HTMLDivElement>(null);

    const selected = items.find(i => i.id === value);
    const filtered = search
        ? items.filter(i => i.name.toLowerCase().includes(search.toLowerCase()) || String(i.id).includes(search))
        : items;

    useEffect(() => {
        if (!open) return;
        const handler = (e: MouseEvent) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [open]);

    if (disabled && selected) {
        return <span className={styles.searchSelected}>({selected.id}) {selected.name}</span>;
    }

    return (
        <div className={styles.searchWrap} ref={wrapRef}>
            <input
                className={styles.searchInput}
                type="text"
                placeholder={selected ? `(${selected.id}) ${selected.name}` : (placeholder ?? "Search...")}
                value={search}
                onChange={e => { setSearch(e.target.value); setOpen(true); }}
                onFocus={() => setOpen(true)}
                disabled={disabled}
            />
            {open && filtered.length > 0 && (
                <div className={styles.searchDropdown}>
                    {filtered.map(item => (
                        <div
                            key={item.id}
                            className={`${styles.searchOption}${item.id === value ? ` ${styles.searchOptionActive}` : ""}`}
                            onMouseDown={() => { onChange(item.id); setSearch(""); setOpen(false); }}
                        >
                            <span className={styles.searchOptionId}>({item.id})</span> {item.name}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

// ── Package Section ────────────────────────────────────────

function PackageSection({ packages, packageDeals, productList, onChange, onAdd, onRemove }: {
    packages: PackageGroup[];
    packageDeals: { id: number; name: string; type: string }[];
    productList: { id: number; name: string }[];
    onChange: (pkgIdx: number, updated: PackageGroup) => void;
    onAdd: () => void;
    onRemove: (pkgIdx: number) => void;
}) {
    const [expanded, setExpanded] = useState<number | null>(null);

    return (
        <div className={styles.section}>
            <div className={styles.sectionHeader}>
                <span className={styles.sectionTitle}>Packages / Subsidies</span>
                {packages.length > 0 && <span className={styles.badge}>{fmtAmt(packages.reduce((s, p) => s + (p.unitPrice + p.unitTax) * p.quantity, 0))}</span>}
                <button className={styles.addBtn} onClick={onAdd}>+ Add Package</button>
            </div>
            {packages.map((pkg, pi) => {
                const dealInfo = packageDeals.find(d => d.id === pkg.packageID);
                const isExp = expanded === pi;
                const itemTotal = pkg.items.reduce((s, it) => s + it.unitPrice * it.quantity, 0);
                const itemTaxTotal = pkg.items.reduce((s, it) => s + it.unitTax * it.quantity, 0);

                return (
                    <div key={pkg.groupID} className={styles.pkgCard}>
                        <div className={styles.pkgHeader} onClick={() => setExpanded(isExp ? null : pi)}>
                            <span className={styles.expandIcon}>{isExp ? "\u25BC" : "\u25B6"}</span>
                            <span className={styles.pkgTitle}>
                                {dealInfo ? `(${dealInfo.id}) ${dealInfo.name}` : pkg.packageID ? `Package #${pkg.packageID}` : "New package"}
                            </span>
                            <span className={styles.pkgTotal}>{fmtAmt((pkg.unitPrice + pkg.unitTax) * pkg.quantity)}</span>
                            {pkg.isNew && <button className={styles.removeBtn} onClick={e => { e.stopPropagation(); onRemove(pi); }}>&times;</button>}
                        </div>
                        {isExp && (
                            <div className={styles.pkgBody}>
                                <div className={styles.pkgFields}>
                                    <label className={styles.fieldLabel}>
                                        Package Deal
                                        <SearchableSelect
                                            items={packageDeals}
                                            value={pkg.packageID}
                                            onChange={id => onChange(pi, { ...pkg, packageID: id })}
                                            disabled={!pkg.isNew}
                                            placeholder="Search package..."
                                        />
                                    </label>
                                    <label className={styles.fieldLabel}>
                                        Unit Price
                                        <AmtInput value={pkg.unitPrice} onChange={v => onChange(pi, { ...pkg, unitPrice: v })} />
                                    </label>
                                    <label className={styles.fieldLabel}>
                                        Unit Tax
                                        <AmtInput value={pkg.unitTax} onChange={v => onChange(pi, { ...pkg, unitTax: v })} />
                                    </label>
                                    <label className={styles.fieldLabel}>
                                        Quantity
                                        {/* Allow 0: removing an existing package deal isn't permitted, so
                                            zeroing its quantity is the sanctioned way to drop the reduction
                                            (e.g. after a purchased line tied to the deal is removed). Qty 0
                                            makes (unitPrice × qty) fall out of the head total — preventing
                                            the package-deal amount from being over-refunded. */}
                                        <input className={styles.qtyInput} type="number" min="0" value={pkg.quantity}
                                            onChange={e => { const q = parseInt(e.target.value); onChange(pi, { ...pkg, quantity: Number.isNaN(q) ? 0 : Math.max(0, q) }); }} />
                                    </label>
                                </div>
                                {pkg.items.length > 0 && (
                                    <div className={styles.itemTotals}>
                                        Sub-item totals: {fmtAmt(itemTotal)} + {fmtAmt(itemTaxTotal)} tax
                                    </div>
                                )}
                                <SubItemsTable
                                    items={pkg.items}
                                    products={productList}
                                    onChange={(ii, field, value) => {
                                        const newItems = [...pkg.items];
                                        newItems[ii] = { ...newItems[ii]!, [field]: value };
                                        onChange(pi, { ...pkg, items: newItems });
                                    }}
                                    onAdd={() => onChange(pi, { ...pkg, items: [...pkg.items, { productID: 0, unitPrice: 0, unitTax: 0, quantity: 1 }] })}
                                    onRemove={ii => onChange(pi, { ...pkg, items: pkg.items.filter((_, j) => j !== ii) })}
                                />
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

// ── Promotion Section ──────────────────────────────────────

function PromotionEditSection({ promo, promotionNames, productList, onChange }: {
    promo: PromotionData | null;
    promotionNames: Record<number, string>;
    productList: { id: number; name: string }[];
    onChange: (updated: PromotionData | null) => void;
}) {
    const { toast } = useToast();
    const [code, setCode] = useState(promo?.code ?? "");
    const [validating, setValidating] = useState(false);

    const handleLookup = async () => {
        if (!code.trim()) return;
        setValidating(true);
        try {
            const result = await editCartGetPromotionCode(code.trim());
            if (!result.bError && result.oData) {
                const d = result.oData;
                const existing = promo ?? { code: "", promotionID: 0, promotionCodeID: 0, initPrice: 0, initTax: 0, latePrice: 0, lateTax: 0, items: [], isNew: true };
                onChange({ ...existing, code: d.Code, promotionID: d.PromotionID, promotionCodeID: d.PromotionCodeID, isNew: !promo });
                toast(`Code "${d.Code}" validated`, "success");
            } else {
                toast("Invalid promotion code", "error");
            }
        } finally { setValidating(false); }
    };

    const handleClear = () => { onChange(null); setCode(""); };

    const promoTitle = promo?.promotionID ? promotionNames[promo.promotionID] : null;

    return (
        <div className={styles.section}>
            <div className={styles.sectionHeader}>
                <span className={styles.sectionTitle}>Promotion</span>
                {promo && promo.promotionCodeID > 0 && (
                    <span className={styles.badge}>{fmtAmt(promo.initPrice + promo.initTax + promo.latePrice + promo.lateTax)}</span>
                )}
            </div>
            <div className={styles.promoBody}>
                {/* Code entry */}
                <div className={styles.promoCodeRow}>
                    <input className={styles.codeInput} type="text" placeholder="Promotion code..." value={code}
                        onChange={e => setCode(e.target.value)}
                        onKeyDown={e => { if (e.key === "Enter") handleLookup(); }}
                        disabled={!!promo && !promo.isNew}
                    />
                    {(!promo || promo.isNew) && (
                        <button className={styles.addBtn} onClick={handleLookup} disabled={validating || !code.trim()}>
                            {validating ? "Checking..." : promo ? "Update" : "Apply Code"}
                        </button>
                    )}
                    {promo && <button className={styles.removeBtn} onClick={handleClear} title="Remove promotion">&times;</button>}
                </div>

                {/* Promotion details */}
                {promo && promo.promotionCodeID > 0 && (
                    <>
                        <div className={styles.promoInfo}>
                            {promoTitle && <span>Promotion: <strong>{promoTitle}</strong></span>}
                            <span className={styles.promoIds}>ID: {promo.promotionID} / Code ID: {promo.promotionCodeID}</span>
                        </div>
                        <div className={styles.promoFields}>
                            <label className={styles.fieldLabel}>
                                Init Price
                                <AmtInput value={promo.initPrice} negative onChange={v => onChange({ ...promo, initPrice: v })} />
                            </label>
                            <label className={styles.fieldLabel}>
                                Init Tax
                                <AmtInput value={promo.initTax} negative onChange={v => onChange({ ...promo, initTax: v })} />
                            </label>
                            <label className={styles.fieldLabel}>
                                Late Price
                                <AmtInput value={promo.latePrice} onChange={v => onChange({ ...promo, latePrice: v })} />
                            </label>
                            <label className={styles.fieldLabel}>
                                Late Tax
                                <AmtInput value={promo.lateTax} onChange={v => onChange({ ...promo, lateTax: v })} />
                            </label>
                            <div className={styles.promoTotal}>
                                Total: {fmtAmt(promo.initPrice + promo.initTax + promo.latePrice + promo.lateTax)}
                            </div>
                        </div>
                        <SubItemsTable
                            items={promo.items}
                            products={productList}
                            onChange={(ii, field, value) => {
                                const newItems = [...promo.items];
                                newItems[ii] = { ...newItems[ii]!, [field]: value };
                                onChange({ ...promo, items: newItems });
                            }}
                            onAdd={() => onChange({ ...promo, items: [...promo.items, { productID: 0, unitPrice: 0, unitTax: 0, quantity: 1 }] })}
                            onRemove={ii => onChange({ ...promo, items: promo.items.filter((_, j) => j !== ii) })}
                        />
                    </>
                )}
            </div>
        </div>
    );
}

// ── Payments Summary ───────────────────────────────────────

/** "Yes" / "Portal only" for a payment a refund could come from, blank for the rest. Text, not
 *  colour, carries the verdict; the reason is the tooltip. */
function RefundCell({ verdict }: { verdict: TransactionSettlement["Payments"][number] | undefined }) {
    if (!verdict) return <td className={styles.tdStatus}></td>;
    return verdict.CanRefund
        ? <td className={styles.tdStatus}>Yes</td>
        : <td className={`${styles.tdStatus} ${styles.refundPortalOnly}`} title={verdict.RefundBlockedReason ?? undefined}>Portal only</td>;
}

/** The provider and what it can do from the server, for the Payments header. */
function providerSummary(s: TransactionSettlement): string {
    const refund = s.CanRefund ? (s.PartialRefund ? "full or partial" : "yes") : "in the provider's portal";
    const charge = s.CanCharge ? "yes" : "in the provider's portal";
    return `${s.ProviderName} · refund ${refund} · extra charge ${charge}`;
}

function PaymentsSummary({ payments, settlement: s }: { payments: TransactionChangePayment[]; settlement: TransactionSettlement | null | undefined }) {
    if (payments.length === 0) return null;
    const total = payments.reduce((sum, p) => sum + (p.Price ?? 0) + (p.Tax ?? 0), 0);
    const verdicts = new Map((s?.Payments ?? []).map(v => [v.PaymentID, v]));
    const notes = s ? [s.RefundNote && `Refund: ${s.RefundNote}`, s.ChargeNote && `Extra charge: ${s.ChargeNote}`].filter(Boolean).join("\n") : "";
    return (
        <div className={styles.section}>
            <div className={styles.sectionHeader}>
                <span className={styles.sectionTitle}>Payments</span>
                {s && <span className={styles.providerTag} title={notes || undefined}>{providerSummary(s)}</span>}
                <span className={styles.badge}>{fmtAmt(total)}</span>
            </div>
            <table className={styles.cartTable}>
                <thead><tr><th>Type</th><th className={styles.thR}>Price</th><th className={styles.thR}>Tax</th><th className={styles.thR}>Total</th><th className={styles.thStatus}>Status</th>{s && <th className={styles.thStatus}>Refund</th>}</tr></thead>
                <tbody>
                    {payments.map((p, i) => (
                        <tr key={i}>
                            <td>{p.Type}</td>
                            <td className={styles.tdR}>{fmtAmt(p.Price)}</td>
                            <td className={styles.tdR}>{fmtAmt(p.Tax)}</td>
                            <td className={styles.tdR}>{fmtAmt((p.Price ?? 0) + (p.Tax ?? 0))}</td>
                            <td className={styles.tdStatus}>{p.Status ?? ""}</td>
                            {s && <RefundCell verdict={verdicts.get(p.ID)} />}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

// ── Main Modal ─────────────────────────────────────────────

interface EditCartModalProps {
    open: boolean;
    instantID: string;
    onClose: () => void;
    onSaved?: () => void;
}

export function EditCartModal({ open, instantID, onClose, onSaved }: EditCartModalProps) {
    const { toast } = useToast();
    const { cache } = useAuth();
    /* TremaSport (was CustomerID 94) lets users save without a comment; we
     * substitute "Reason omitted" so the audit log entry isn't blank. All other
     * DBs must enter > 10 characters. Per-database feature flag (see
     * getDbFeatureFlags). Mirrors popupEditCart2.ts:2285. */
    const isTremaSport = !!getDbFeatureFlags(cache).EditCartTremaSport;
    const [container, setContainer] = useState<UpdateTransactionContainer | null>(null);
    const [head, setHead] = useState<TransactionChangeHead | null>(null);
    const [packages, setPackages] = useState<PackageGroup[]>([]);
    const [promotion, setPromotion] = useState<PromotionData | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [comment, setComment] = useState("");
    /* Two-phase modal: user edits the cart, then must verify (with comment +
     * issue acknowledgment) before the change is committed. Mirrors
     * WebAdmin2 popupEditCart2.ts, where DoSave hands off to a ConfirmPanel
     * before UpdateTransaction is called. */
    const [phase, setPhase] = useState<"edit" | "confirm">("edit");
    const [pendingIntent, setPendingIntent] = useState<{
        action: SettleAction;
        finalContainer: UpdateTransactionContainer;
        description: string;
        hasIssues: boolean;
        issues: string[];
        /** Refund total the operator confirms (−diff); 0 unless action is refund. */
        refundAmount: number;
        refundSources: RefundSource[];
        /** The SUBSIDY payment rows the save will insert, one per subsidy out of step. */
        subsidyAdjustments: SubsidyAdjustment[];
        /** What the server will not settle and where the operator does it instead. */
        settleNote?: string;
    } | null>(null);
    /** Payments the refund comes from, in the operator's order. Sent as RefundPaymentOrder. */
    const [refundPlan, setRefundPlan] = useState<RefundPlanEntry[]>([]);
    const [issuesAcked, setIssuesAcked] = useState(false);

    // Cache lookups
    const { productNames, productList } = useMemo(() => {
        const map: Record<number, string> = {};
        const list: { id: number; name: string }[] = [];
        const products = cache?.CommonData?.Product as Array<Record<string, unknown>> | undefined;
        if (products) for (const p of products) {
            const id = (p.ProductID ?? p.iProductID) as number | undefined;
            const name = (p.Name ?? p.sName) as string | undefined;
            if (id != null && name) { map[id] = name; list.push({ id, name }); }
        }
        return { productNames: map, productList: list };
    }, [cache]);

    const packageDeals = useMemo(() => {
        const common = cache?.CommonData?.Common as Array<{ TypeName?: string; Title?: string; Value?: string; isDeleted?: boolean }> | undefined;
        if (!common) return [];
        return common
            .filter(c => (c.TypeName ?? "").toLowerCase() === "packagedeal" && !c.isDeleted)
            .map(c => {
                // Title = numeric ID, Value = "displayName" or "displayName:type"
                const id = parseInt(String(c.Title ?? "0"), 10);
                const raw = String(c.Value ?? "");
                const parts = raw.split(":");
                return { id, name: parts[0] ?? "", type: parts[1] ?? "D" };
            })
            .filter(d => d.id > 0 && d.name.length > 0);
    }, [cache]);

    const promotionNames = useMemo(() => {
        const map: Record<number, string> = {};
        const promos = cache?.Promotions as Array<{ PromotionID: number; Title: string }> | undefined;
        if (promos) for (const p of promos) map[p.PromotionID] = p.Title;
        return map;
    }, [cache]);

    /* Admin_RefundFilter: payments whose ResultData match are refused by the server
     * (internalWebRefundTransaction). Honoured here only when the setting reaches the cache. */
    const refundFilter = useMemo(() => {
        const v = cache?.ServerSettings?.find(s => s.Key === "Admin_RefundFilter")?.Value;
        return typeof v === "string" && v.length > 0 ? v : undefined;
    }, [cache]);

    // ── Load data ──────────────────────────────────────────

    // Fetch on open. Inlined (not a useCallback) so the effect's dependency
    // array stays at `[open, instantID]` — the previous `[open, loadData]`
    // setup re-fired whenever `loadData`'s identity churned (e.g. when
    // `toast` from useToast() returned a new function reference), causing
    // the modal to hit `getEditTransaction` twice on every open. A cancel
    // flag also discards stale state writes after StrictMode's dev double-
    // mount or a rapid close/reopen.
    const toastRef = useRef(toast);
    toastRef.current = toast;
    // Dedupe in-flight fetch by instantID so StrictMode's dev double-mount (or
    // any rapid re-render of the parent) doesn't fire two HTTP calls. The
    // cancel flag below already discarded stale state writes, but the network
    // request still went out twice. Reusing the in-flight promise collapses
    // them to one. Cleared on completion so reopens after a finish refetch.
    const inFlightRef = useRef<{ id: string; promise: ReturnType<typeof getEditTransaction> } | null>(null);
    useEffect(() => {
        if (!open) return;
        setPhase("edit");
        setPendingIntent(null);
        setIssuesAcked(false);
        setComment("");
        setLoading(true);
        let cancelled = false;
        let promise: ReturnType<typeof getEditTransaction>;
        if (inFlightRef.current?.id === instantID) {
            promise = inFlightRef.current.promise;
        } else {
            promise = getEditTransaction(instantID);
            inFlightRef.current = { id: instantID, promise };
        }
        (async () => {
            try {
                const result = await promise;
                if (cancelled) return;
                if (!result.bError && result.oData) {
                    setContainer(result.oData);
                    const h = structuredClone(result.oData.Head);
                    if (!h.Rows) h.Rows = [];
                    setHead(h);

                    // Parse packages from rows
                    const pkgMap = new Map<number, PackageGroup>();
                    const promoData: PromotionData = { code: "", promotionID: 0, promotionCodeID: 0, initPrice: 0, initTax: 0, latePrice: 0, lateTax: 0, items: [], isNew: false };
                    let hasPromo = false;

                    (h.Rows ?? []).forEach((row, idx) => {
                        if ([23, 36, 40, 42].includes(row.RowType)) {
                            const gid = row.ProductItemID ?? row.RowID ?? 0;
                            const type = row.RowType === 23 ? "D" : row.RowType === 36 ? "S" : row.RowType === 40 ? "R" : "T";
                            pkgMap.set(gid, {
                                groupID: gid, packageID: row.Product, packageType: type,
                                unitPrice: row.InitialAmount ?? 0, unitTax: row.InitialTax ?? 0,
                                quantity: row.Quantity ?? 1, items: [], parentRowIdx: idx, isNew: false,
                            });
                        }
                        if ([24, 37, 41, 43].includes(row.RowType)) {
                            const gid = row.ProductItemID ?? 0;
                            const pkg = pkgMap.get(gid);
                            if (pkg) pkg.items.push({ productID: row.Product, unitPrice: row.InitialAmount ?? 0, unitTax: row.InitialTax ?? 0, quantity: row.Quantity ?? 1, rowIdx: idx });
                        }
                        if (row.RowType === 6) {
                            hasPromo = true;
                            promoData.promotionID = row.Product;
                            promoData.promotionCodeID = row.ProductItemID ?? 0;
                            promoData.initPrice = row.InitialAmount ?? 0;
                            promoData.initTax = row.InitialTax ?? 0;
                            promoData.latePrice = row.LateAmount ?? 0;
                            promoData.lateTax = row.LateTax ?? 0;
                            promoData.parentRowIdx = idx;
                        }
                        if (row.RowType === 44) {
                            promoData.items.push({ productID: row.Product, unitPrice: row.InitialAmount ?? 0, unitTax: row.InitialTax ?? 0, quantity: row.Quantity ?? 1, rowIdx: idx });
                        }
                    });

                    setPackages(Array.from(pkgMap.values()));
                    setPromotion(hasPromo ? promoData : null);
                } else {
                    toastRef.current(result.sMessage ?? "Failed to load transaction", "error");
                }
            } finally {
                if (!cancelled) setLoading(false);
                if (inFlightRef.current?.promise === promise) inFlightRef.current = null;
            }
        })();
        return () => { cancelled = true; };
    }, [open, instantID]);

    // ── Simple row helpers ─────────────────────────────────

    const simpleRows = useMemo(() => {
        const purchases: { rows: TransactionChangeRow[]; indices: number[] } = { rows: [], indices: [] };
        const rentals: { rows: TransactionChangeRow[]; indices: number[] } = { rows: [], indices: [] };
        (head?.Rows ?? []).forEach((row, idx) => {
            const cat = classifyRow(row);
            if (cat === "purchase") { purchases.rows.push(row); purchases.indices.push(idx); }
            if (cat === "rental") { rentals.rows.push(row); rentals.indices.push(idx); }
        });
        return { purchases, rentals };
    }, [head]);

    const handleRowChange = useCallback((idx: number, field: string, value: unknown) => {
        setHead(prev => {
            if (!prev) return prev;
            const rows = [...prev.Rows];
            rows[idx] = { ...rows[idx]!, [field]: value };
            return { ...prev, Rows: rows };
        });
    }, []);

    // Nested edit for the rental/reserve RentalInfo block (Max charge for both;
    // Late Period price/rate for reserve). The recalc reads RentalInfo each render,
    // so these edits re-derive Late Periods / Final immediately, and buildFinalRows
    // preserves the edited RentalInfo via its row spread.
    const handleRentalInfoChange = useCallback((idx: number, field: string, value: number) => {
        setHead(prev => {
            if (!prev) return prev;
            const rows = [...prev.Rows];
            const r = rows[idx]!;
            rows[idx] = { ...r, RentalInfo: { ...(r.RentalInfo ?? {}), [field]: value } as TransactionRentalInfo };
            return { ...prev, Rows: rows };
        });
    }, []);

    const handleRemoveRow = useCallback((idx: number) => {
        setHead(prev => prev ? { ...prev, Rows: prev.Rows.filter((_, i) => i !== idx) } : prev);
    }, []);

    const handleAddPurchase = useCallback(() => {
        // RowType 3 = purchase, matching what WebAdmin2 writes for new items
        // (popupEditCart2.ts:662) and what the kiosk itself produces; 2 is a
        // legacy read-side alias only. Status "New" is a UI marker — replaced
        // with "OK" (and the negative RowID stripped) in buildFinalRows.
        setHead(prev => {
            if (!prev) return prev;
            return { ...prev, Rows: [...prev.Rows, { RowID: nextNewId--, RowType: 3, Product: 0, InitialAmount: 0, InitialTax: 0, Quantity: 1, Status: "New", Sold: false }] };
        });
    }, []);

    // ── Build final container for save ─────────────────────

    /* Build the row list sent to the server. Two invariants, both learned the
     * hard way (S_UpdateTransaction_UpdateFull_SP MERGE writes every column):
     *
     *  1. EXISTING rows must be the ORIGINAL server row spread + edited fields
     *     only. WebAdmin2 keeps the very object it parsed (SetRow) and mutates
     *     fields in place; rebuilding from scratch drops DeliverDate /
     *     ReturnDate / DueByDate / Bias / ReturnKiosk / LateAmount / Sold, and
     *     the MERGE then NULLs those columns and flips bSold on rows that were
     *     never touched in the UI.
     *
     *  2. NEW rows mirror WebAdmin2's shapes (popupEditCart2.ts:649-906):
     *     server-local dates, DueByDate = ZERO_DATE sentinel, Status "OK",
     *     Sold true (promo parent: false), and no RowID (MERGE treats
     *     unmatched RowID as insert; legacy never sends one). */
    const serverTz = (cache as { ServerTimezoneOffset?: number } | null)?.ServerTimezoneOffset ?? 0;
    const buildFinalRows = useCallback((): TransactionChangeRow[] => {
        if (!head) return [];
        const now = serverNowIso(serverTz);
        // Keep simple rows (purchases, rentals, other non-sub-item types).
        // For rental/reserve rows with a RentalInfo block, write back the
        // computed late fee (LateAmount/LateTax) per WebAdmin2 — InitialAmount/
        // InitialTax stay as edited (rental base), RentalInfo is left intact,
        // and Sold is NOT changed here. Non-applicable rental rows pass through.
        const rows = (head.Rows ?? [])
            .filter(r => !isSubItemType(r.RowType) && r.RowType !== 6 && ![23, 36, 40, 42].includes(r.RowType))
            .map(r => {
                // New purchases (negative UI RowID): legacy insert shape.
                if (classifyRow(r) === "purchase" && (r.RowID ?? 0) <= 0) {
                    return {
                        ...r, RowID: undefined, Status: "OK", Sold: true,
                        DeliverDate: now, ReturnDate: now, DueByDate: ZERO_DATE,
                        LateAmount: 0, LateTax: 0,
                    };
                }
                // Refund marker rows: legacy zeroes their amounts on save
                // (popupEditCart2.ts:953-958).
                if (r.RowType === 16) {
                    return { ...r, InitialAmount: 0, InitialTax: 0, LateAmount: 0, LateTax: 0 };
                }
                if (classifyRow(r) !== "rental") return r;
                const rc = computeRentalRecalc(r);
                if (!rc.applicable) return r;
                return { ...r, LateAmount: rc.latePrice, LateTax: rc.lateTax };
            });

        // Add packages. New groups get a positive sequential GroupID
        // (max existing + 1, legacy popupEditCart2.ts:749-756) — the UI's
        // negative placeholder would otherwise land in iRFID.
        let nextGroupID = packages.reduce((m, p) => Math.max(m, p.groupID > 0 ? p.groupID : 0), 0) + 1;
        for (const pkg of packages) {
            const parentType = pkg.packageType === "D" ? 23 : pkg.packageType === "S" ? 36 : pkg.packageType === "R" ? 40 : 42;
            const childType = pkg.packageType === "D" ? 24 : pkg.packageType === "S" ? 37 : pkg.packageType === "R" ? 41 : 43;
            const groupID = pkg.groupID > 0 ? pkg.groupID : nextGroupID++;
            const orig = pkg.parentRowIdx !== undefined ? head.Rows[pkg.parentRowIdx] : undefined;
            rows.push(orig ? {
                ...orig,
                Product: pkg.packageID, ProductItemID: groupID,
                InitialAmount: pkg.unitPrice, InitialTax: pkg.unitTax, Quantity: pkg.quantity,
            } : {
                RowType: parentType, Product: pkg.packageID, ProductItemID: groupID,
                InitialAmount: pkg.unitPrice, InitialTax: pkg.unitTax, Quantity: pkg.quantity,
                DeliverDate: now, ReturnDate: now, DueByDate: ZERO_DATE,
                Status: "OK", Sold: true,
            });
            for (const it of pkg.items) {
                const iorig = it.rowIdx !== undefined ? head.Rows[it.rowIdx] : undefined;
                rows.push(iorig ? {
                    ...iorig,
                    Product: it.productID, ProductItemID: groupID,
                    InitialAmount: it.unitPrice, InitialTax: it.unitTax, Quantity: it.quantity,
                } : {
                    RowType: childType, Product: it.productID, ProductItemID: groupID,
                    InitialAmount: it.unitPrice, InitialTax: it.unitTax, Quantity: it.quantity,
                    DeliverDate: now, ReturnDate: now, DueByDate: ZERO_DATE,
                    Status: "OK", Sold: true,
                });
            }
        }

        // Add promotion. Legacy quirks preserved: new promo parent gets
        // DueByDate = now (not ZERO_DATE) and Sold false; new promo ITEMS get
        // Sold true and NO ProductItemID (popupEditCart2.ts:858-893).
        if (promotion && promotion.promotionCodeID > 0) {
            const orig = promotion.parentRowIdx !== undefined ? head.Rows[promotion.parentRowIdx] : undefined;
            rows.push(orig ? {
                ...orig,
                Product: promotion.promotionID, ProductItemID: promotion.promotionCodeID,
                InitialAmount: promotion.initPrice, InitialTax: promotion.initTax,
                LateAmount: promotion.latePrice, LateTax: promotion.lateTax,
            } : {
                RowType: 6, Product: promotion.promotionID, ProductItemID: promotion.promotionCodeID,
                InitialAmount: promotion.initPrice, InitialTax: promotion.initTax,
                LateAmount: promotion.latePrice, LateTax: promotion.lateTax,
                DeliverDate: now, DueByDate: now,
                Quantity: 1, Status: "OK", Sold: false,
            });
            for (const it of promotion.items) {
                const iorig = it.rowIdx !== undefined ? head.Rows[it.rowIdx] : undefined;
                rows.push(iorig ? {
                    ...iorig,
                    Product: it.productID,
                    InitialAmount: it.unitPrice, InitialTax: it.unitTax, Quantity: it.quantity,
                } : {
                    RowType: 44, Product: it.productID,
                    InitialAmount: it.unitPrice, InitialTax: it.unitTax, Quantity: it.quantity,
                    DeliverDate: now, ReturnDate: now, DueByDate: ZERO_DATE,
                    Status: "OK", Sold: true,
                });
            }
        }

        return rows;
    }, [head, packages, promotion, serverTz]);

    // ── Totals ─────────────────────────────────────────────

    const totals = useMemo(() => {
        if (!head) return { cart: 0, paid: 0, diff: 0 };
        let cart = 0;
        // Simple rows
        for (const row of (head.Rows ?? []).filter(r => !isSubItemType(r.RowType) && r.RowType !== 6 && ![23, 36, 40, 42].includes(r.RowType))) {
            // Rental/reserve rows (1/45) contribute the computed final (incl.
            // buy when sold); computeRentalRecalc returns an Initial+Late
            // passthrough for the non-applicable case, so call it uniformly.
            if (classifyRow(row) === "rental") {
                const rc = computeRentalRecalc(row);
                cart += rc.finalPrice + rc.finalTax;
                continue;
            }
            const q = row.Quantity ?? 1;
            cart += ((row.InitialAmount ?? 0) + (row.InitialTax ?? 0) + (row.LateAmount ?? 0) + (row.LateTax ?? 0)) * q;
        }
        // Packages
        for (const pkg of packages) cart += (pkg.unitPrice + pkg.unitTax) * pkg.quantity;
        // Promotion
        if (promotion) cart += promotion.initPrice + promotion.initTax + promotion.latePrice + promotion.lateTax;

        // Rounded to cents, SUBSIDY excluded from paid — see cartSettlement.ts.
        return settlement(cart, head.Payments ?? []);
    }, [head, packages, promotion]);

    // ── Verification issues (client-side) ──────────────────
    /* Mirrors WebAdmin2's verificationIssues in popupEditCart2.ts:818-906:
     * sub-item totals (Σ unitPrice × qty) must match the parent's UnitPrice /
     * InitPrice. Compared unit-level (NOT × parent.Quantity), and only when
     * sub-items exist. Late price/tax are NOT cross-checked (legacy parity).
     * Tolerance < 0.005 to absorb floating-point drift on cent amounts. */
    const clientIssues = useMemo(() => {
        const out: string[] = [];
        const EPS = 0.005;
        const typeLabel = (t: string) => t === "D" ? "Package" : t === "S" ? "Subsidy" : t === "R" ? "External" : "Tender";
        for (const pkg of packages) {
            if (pkg.items.length === 0) continue;
            const itPrice = pkg.items.reduce((s, it) => s + it.unitPrice * it.quantity, 0);
            const itTax = pkg.items.reduce((s, it) => s + it.unitTax * it.quantity, 0);
            const type = typeLabel(pkg.packageType);
            const existingRowID = pkg.parentRowIdx !== undefined ? head?.Rows[pkg.parentRowIdx]?.RowID : undefined;
            const idRef = pkg.isNew || !existingRowID ? `New#${pkg.packageID}` : String(existingRowID);
            if (Math.abs(itPrice - pkg.unitPrice) >= EPS) {
                out.push(`${type} ${idRef}: reporting price doesn't match ${type.toLowerCase()}, ${fmtAmt(itPrice)} ${fmtAmt(pkg.unitPrice)}`);
            }
            if (Math.abs(itTax - pkg.unitTax) >= EPS) {
                out.push(`${type} ${idRef}: reporting tax doesn't match ${type.toLowerCase()}, ${fmtAmt(itTax)} ${fmtAmt(pkg.unitTax)}`);
            }
        }
        if (promotion && promotion.promotionCodeID > 0 && promotion.items.length > 0) {
            const itPrice = promotion.items.reduce((s, it) => s + it.unitPrice * it.quantity, 0);
            const itTax = promotion.items.reduce((s, it) => s + it.unitTax * it.quantity, 0);
            if (Math.abs(itPrice - promotion.initPrice) >= EPS) {
                out.push(`Promotion: reporting price doesn't match, ${fmtAmt(itPrice)} ${fmtAmt(promotion.initPrice)}`);
            }
            if (Math.abs(itTax - promotion.initTax) >= EPS) {
                out.push(`Promotion: reporting tax doesn't match, ${fmtAmt(itTax)} ${fmtAmt(promotion.initTax)}`);
            }
        }

        /* Product overuse: a product consumed by a package/subsidy/promotion
         * line must still be purchased in at least that quantity. Mirrors
         * WebAdmin2's productUsage check (popupEditCart2.ts:924-929) — e.g.
         * zeroing a purchased product's quantity while its package-deal
         * reduction still references it (purchase 0, package 1 → -1). Without
         * this the cart silently keeps the discount and over-refunds. WebAdminCC
         * has no separate "refund adjustment" concept, so reduced quantity is
         * the purchase count directly. */
        const usage = new Map<number, { purchase: number; pkg: number; subsidy: number; promo: number }>();
        const bump = (pid: number, k: "purchase" | "pkg" | "subsidy" | "promo", n: number) => {
            if (pid <= 0 || !n) return;
            const u = usage.get(pid) ?? { purchase: 0, pkg: 0, subsidy: 0, promo: 0 };
            u[k] += n;
            usage.set(pid, u);
        };
        for (const row of head?.Rows ?? []) {
            if (classifyRow(row) === "purchase") bump(row.Product, "purchase", row.Quantity ?? 1);
        }
        for (const pkg of packages) {
            for (const it of pkg.items) {
                bump(it.productID, pkg.packageType === "S" ? "subsidy" : "pkg", it.quantity * pkg.quantity);
            }
        }
        if (promotion && promotion.promotionCodeID > 0) {
            for (const it of promotion.items) bump(it.productID, "promo", it.quantity);
        }
        for (const [pid, u] of usage) {
            if (u.purchase - u.pkg < 0 || u.purchase - u.subsidy < 0 || u.purchase - u.promo < 0) {
                const name = productNames[pid];
                out.push(`Product ${pid}${name ? ` (${name})` : ""} overused: ${u.purchase} purchase, ${u.pkg} package, ${u.subsidy} subsidy, ${u.promo} promotion`);
            }
        }

        /* Remaining WebAdmin2 parity checks (popupEditCart2.ts:672-971). */
        // Purchases: invalid product; new items need price > 0, tax >= 0.
        for (const row of head?.Rows ?? []) {
            if (classifyRow(row) !== "purchase") continue;
            const ref = (row.RowID ?? 0) > 0 ? String(row.RowID) : `New#${row.Product}`;
            if (row.Product <= 0 || !productNames[row.Product]) {
                out.push(`Product ${ref}: invalid product id ${row.Product}`);
            }
            if ((row.RowID ?? 0) <= 0) {
                if ((row.InitialAmount ?? 0) <= 0) out.push(`Product ${ref}: new item with price less than or equal to 0`);
                if ((row.InitialTax ?? 0) < 0) out.push(`Product ${ref}: new item with tax less than 0`);
            }
        }
        // Packages: item products must exist; new packages need quantity and a
        // NEGATIVE price (they are reductions).
        for (const pkg of packages) {
            const type = typeLabel(pkg.packageType);
            const existingRowID = pkg.parentRowIdx !== undefined ? head?.Rows[pkg.parentRowIdx]?.RowID : undefined;
            const idRef = pkg.isNew || !existingRowID ? `New#${pkg.packageID}` : String(existingRowID);
            for (const it of pkg.items) {
                if (it.productID <= 0 || !productNames[it.productID]) out.push(`${type} ${idRef}: invalid item product #${it.productID}`);
            }
            if (pkg.isNew) {
                if (pkg.quantity === 0) {
                    out.push(`${type} ${idRef}: new item with no quantity`);
                } else {
                    if (pkg.unitPrice >= 0) out.push(`${type} ${idRef}: new item with price greater than or equal to 0`);
                    if (pkg.unitTax > 0) out.push(`${type} ${idRef}: new item with tax greater than 0`);
                }
            }
        }
        if (promotion && promotion.promotionCodeID > 0) {
            for (const it of promotion.items) {
                if (it.productID <= 0 || !productNames[it.productID]) out.push(`Promotion: invalid item product #${it.productID}`);
            }
            if (promotion.isNew && promotion.initPrice + promotion.initTax + promotion.latePrice + promotion.lateTax >= 0) {
                out.push(`Promotion: new promotion with no reduction, ${fmtAmt(promotion.initPrice + promotion.latePrice)} price, ${fmtAmt(promotion.initTax + promotion.lateTax)} tax`);
            }
        }
        // Clearing an existing promotion is a no-op server-side: the MERGE in
        // S_UpdateTransaction_UpdateFull_SP never deletes rows, so the promo
        // row would silently survive while the head total (and any refund)
        // was computed without it. Same limitation as WebAdmin2.
        const hadPromo = (container?.Head.Rows ?? []).some(r => r.RowType === 6);
        if (hadPromo && !promotion) out.push("Promotion: promotion removal not supported");

        // Final head totals below zero (popupEditCart2.ts:967-971).
        const t = computeHeadTotals(buildFinalRows());
        if (t.initAmt < 0) out.push(`Final initial price is below zero ${fmtAmt(t.initAmt)}`);
        if (t.initTax < 0) out.push(`Final initial tax is below zero ${fmtAmt(t.initTax)}`);
        if (t.lateAmt < 0) out.push(`Final late price is below zero ${fmtAmt(t.lateAmt)}`);
        if (t.lateTax < 0) out.push(`Final late tax is below zero ${fmtAmt(t.lateTax)}`);
        if (t.initAmt + t.initTax + t.lateAmt + t.lateTax < 0) out.push(`Final amount is below zero ${fmtAmt(t.initAmt + t.initTax + t.lateAmt + t.lateTax)}`);
        return out;
    }, [packages, promotion, head, productNames, container, buildFinalRows]);

    const allIssues = useMemo(() => {
        return [...(container?.Issues ?? []), ...clientIssues];
    }, [container, clientIssues]);

    // ── Save flow ──────────────────────────────────────────

    /* Label for the *primary* save button. When the cart has a non-zero
     * diff, the primary settles it (charge or refund); when the diff is
     * zero there's nothing to settle, so the primary becomes a plain Save
     * (no separate settle button is shown — see footerLeft). Mirrors
     * WebAdmin2's edit panel two-button setup at popupEditCart2.ts:2406. */
    /* What the provider can do decides the button: a refund or charge the server cannot make is
     * not offered — the cart is saved and the note says to settle in the provider's portal
     * (cartSettlement.ts settleIntent; the server is the authority, Domain/PaymentCapabilities.vb). */
    const intent = useMemo(() => settleIntent(
        totals.diff,
        totals.diff < 0 ? refundSources(head?.Payments ?? [], refundFilter, container?.Settlement) : [],
        container?.Settlement,
    ), [totals.diff, head, refundFilter, container]);
    const settleLabel = intent.label;

    /** Build the final container snapshot from the current edit state.
     *  Used by both save buttons — the only difference between them is
     *  what `Action` gets stamped in the confirm step. */
    const buildFinalContainer = useCallback((): UpdateTransactionContainer | null => {
        if (!container || !head) return null;
        const finalRows = buildFinalRows();
        const finalHead: TransactionChangeHead = { ...head, Rows: finalRows };
        // Head totals feed Customer_Transaction_Head (iPrice/iTax) verbatim
        // and are the refund/charge target — see computeHeadTotals for the
        // exact row-type semantics (sub-item children and subsidy/tender
        // parents excluded; miscounting here over-refunds real money).
        const t = computeHeadTotals(finalRows);
        finalHead.InitialAmount = t.initAmt;
        finalHead.InitialTax = t.initTax;
        finalHead.LateAmount = t.lateAmt;
        finalHead.LateTax = t.lateTax;
        return { ...container, Head: finalHead };
    }, [container, head, buildFinalRows]);

    /** Prepare the confirm phase. `settle = true` means the chosen button
     *  was the primary settle-the-diff one (charge/refund/empty-charge).
     *  `settle = false` is the secondary "Save" button: commit changes
     *  without attempting a charge or refund regardless of diff. Mirrors
     *  WebAdmin2's `DoSave(action: boolean)` (popupEditCart2.ts:2458). */
    const prepareConfirm = (settle: boolean) => {
        const finalContainer = buildFinalContainer();
        if (!finalContainer) return;
        // diff == 0 → "save" (EmptyCharge in WebAdmin2 → Action="save"). A difference the provider
        // cannot settle is "save" too: the cart is stored, the money moves in the provider's portal.
        const action: SettleAction = settle ? intent.action : "save";
        const settleNote = settle ? intent.note : undefined;
        const total = totals.cart;
        let description: string;
        if (action === "refund") {
            description = `Proceeding will issue a refund of ${fmtAmt(-totals.diff)}, giving a transaction total of ${fmtAmt(total)}.`;
        } else if (action === "charge") {
            description = `Proceeding will set the transaction to a payment pending state, charging an additional ${fmtAmt(totals.diff)}, giving a transaction total of ${fmtAmt(total)}.`;
        } else if (settle && totals.diff !== 0) {
            description = `Proceeding will save the cart, giving a transaction total of ${fmtAmt(total)}.`;
        } else if (settle) {
            description = `Proceeding will set the transaction to a payment pending state, giving a transaction total of ${fmtAmt(total)}.`;
        } else {
            description = `Note that the transaction may still be charged based on any changes if it already has a payment pending. The new transaction total is set to ${fmtAmt(total)}.`;
        }
        /* Subsidy + external-tender adjustment, matching popupEditCart2.ts:2348.
         * RowType 36 = subsidy package, 42 = external tender package. WebAdmin2
         * subtracts (InitialAmount + InitialTax) * Quantity into a running
         * `subsidy` (so it ends up negative when present) and reports both the
         * net total after applying it and the subsidy figure itself. */
        let subsidy = 0;
        for (const r of finalContainer.Head.Rows ?? []) {
            if (r.RowType === 36 || r.RowType === 42) {
                subsidy -= ((r.InitialAmount ?? 0) + (r.InitialTax ?? 0)) * (r.Quantity ?? 1);
            }
        }
        if (subsidy !== 0) {
            description += ` And ${fmtAmt(total - subsidy)} after ${fmtAmt(subsidy)} subsidy and external tender.`;
        }
        const hasIssues = allIssues.length > 0;
        const payments = finalContainer.Head.Payments ?? [];
        const refundAmount = action === "refund" ? Math.round(-totals.diff * 100) / 100 : 0;
        const sources = action === "refund" ? refundSources(payments, refundFilter, container?.Settlement) : [];
        // Default = the server's own order, all ticked; blocked payments listed last, unticked.
        setRefundPlan([
            ...defaultRefundOrder(sources.filter(s => !s.blocked), refundAmount).map(s => ({ id: s.id, selected: true })),
            ...sources.filter(s => s.blocked).map(s => ({ id: s.id, selected: false })),
        ]);
        const adjustments = subsidyAdjustments(finalContainer.Head.Rows ?? [], payments);
        setPendingIntent({
            action, finalContainer, description, hasIssues, issues: allIssues,
            refundAmount, refundSources: sources, subsidyAdjustments: adjustments,
            ...(settleNote ? { settleNote } : {}),
        });
        setIssuesAcked(false);
        setComment("");
        setPhase("confirm");
    };

    const cancelConfirm = () => {
        setPhase("edit");
        setPendingIntent(null);
    };

    const commentValid = isTremaSport || comment.trim().length > 10;
    const ackOk = !pendingIntent?.hasIssues || issuesAcked;

    const doConfirm = async () => {
        if (!pendingIntent) return;
        if (!commentValid) { toast("Comment is required and must exceed 10 characters", "error"); return; }
        if (!ackOk) { toast("Acknowledge the listed issues before proceeding", "error"); return; }
        const refundOrder = pendingIntent.action === "refund"
            ? refundPlan.filter(e => e.selected).map(e => e.id)
            : undefined;
        if (refundOrder && refundOrder.length === 0 && pendingIntent.refundSources.some(s => !s.blocked)) {
            toast("Select at least one payment to refund from", "error");
            return;
        }
        setSaving(true);
        try {
            // TremaSport: blank comment is allowed and replaced with the
            // legacy "Reason omitted" placeholder so the audit log isn't
            // empty (popupEditCart2.ts:2287).
            const trimmed = comment.trim();
            const finalComment = trimmed.length === 0 && isTremaSport ? "Reason omitted" : comment;
            const updated: UpdateTransactionContainer = {
                ...pendingIntent.finalContainer,
                Comment: finalComment,
                Action: pendingIntent.action,
                RefundPaymentOrder: refundOrder,
                IssuesAcknowleded: pendingIntent.hasIssues
                    ? new Date().toISOString()
                    : pendingIntent.finalContainer.IssuesAcknowleded,
            };
            const result = await saveEditTransaction(updated);
            if (result.bError) toast(result.sMessage ?? "Failed to save", "error");
            else if (result.eErrorType === ErrorType.PartialSuccessWithMessage) {
                // The edit committed and the charge or refund fell short. Closing on "Transaction
                // updated" here used to hide a failed refund from the operator entirely.
                toast(result.sMessage ?? "Transaction updated, but the payment step did not complete", "warning");
                onSaved?.();
                onClose();
            }
            else { toast("Transaction updated", "success"); onSaved?.(); onClose(); }
        } finally { setSaving(false); }
    };

    /* Modal footer / primary-action wiring is phase-aware:
     *   edit    → primary = settle (Save & Charge/Refund/Pay), footerLeft = Save (no settle)
     *   confirm → primary = Confirm, footerLeft = Back, body shows verification UI */
    const saveLabel = phase === "confirm" ? "Confirm" : settleLabel;
    const onModalSave = phase === "confirm" ? doConfirm : () => prepareConfirm(true);

    /* footerLeft + readOnly disable interplay:
     *   - readOnly hides the Save button; we still want footerLeft visible
     *     so the Close button is reachable. GridEditModal already keeps
     *     footerLeft regardless of readOnly. */
    /* footerLeft holds a *secondary* action: in edit phase, save without
     * settling the diff (only meaningful when diff != 0); in confirm
     * phase, go back to edit. When diff is zero we drop the secondary
     * save altogether — the primary already does the only sensible thing. */
    const footerLeft = !head ? null : phase === "confirm" ? (
        <Button variant="secondary" onClick={cancelConfirm} disabled={saving}>← Back</Button>
    ) : totals.diff !== 0 && intent.action !== "save" ? (
        <Button variant="secondary" onClick={() => prepareConfirm(false)} disabled={saving}>Save Without Settling</Button>
    ) : null;

    return (
        <GridEditModal
            open={open}
            title={`Edit Cart — ${instantID}`}
            wide
            loading={loading}
            saving={saving}
            readOnly={!head}
            cancelLabel={!head ? "Close" : "Cancel"}
            saveLabel={saveLabel}
            footerLeft={footerLeft}
            onClose={onClose}
            onSave={onModalSave}
        >
            <>
                {!loading && !head && <div className={styles.loading}>No transaction data available</div>}
                {!loading && head && phase === "edit" && (
                    <div className={styles.container}>
                        <SimpleRowsSection title="Purchases" category="purchase"
                            rows={simpleRows.purchases.rows} indices={simpleRows.purchases.indices}
                            productNames={productNames} productList={productList} kioskID={head.KioskID}
                            onRowChange={handleRowChange} onRemoveRow={handleRemoveRow} onAddRow={handleAddPurchase} />

                        <RentalsSection title="Rentals"
                            rows={simpleRows.rentals.rows} indices={simpleRows.rentals.indices}
                            productNames={productNames} productList={productList}
                            onRowChange={handleRowChange} onRentalInfoChange={handleRentalInfoChange} />

                        <PackageSection packages={packages} packageDeals={packageDeals} productList={productList}
                            onChange={(pi, updated) => setPackages(prev => prev.map((p, i) => i === pi ? updated : p))}
                            onAdd={() => setPackages(prev => [...prev, {
                                groupID: nextNewId--, packageID: 0, packageType: "D",
                                unitPrice: 0, unitTax: 0, quantity: 1, items: [], isNew: true,
                            }])}
                            onRemove={pi => setPackages(prev => prev.filter((_, i) => i !== pi))} />

                        <PromotionEditSection promo={promotion} promotionNames={promotionNames} productList={productList} onChange={setPromotion} />

                        <PaymentsSummary payments={head.Payments ?? []} settlement={container?.Settlement} />

                        {/* Summary bar */}
                        <div className={styles.summaryBar}>
                            <div className={styles.summaryCell}>
                                <span className={styles.summaryLabel}>Cart</span>
                                <span className={styles.summaryValue}>{fmtAmt(totals.cart)}</span>
                            </div>
                            <div className={styles.summaryCell}>
                                <span className={styles.summaryLabel}>Paid</span>
                                <span className={styles.summaryValue}>{fmtAmt(totals.paid)}</span>
                            </div>
                            <div className={`${styles.summaryCell} ${totals.diff > 0 ? styles.summaryCharge : totals.diff < 0 ? styles.summaryRefund : styles.summaryBalanced}`}>
                                <span className={styles.summaryLabel}>{totals.diff > 0 ? "Charge" : totals.diff < 0 ? "Refund" : "Balanced"}</span>
                                <span className={styles.summaryValue}>{fmtAmt(Math.abs(totals.diff))}</span>
                            </div>
                        </div>

                        {intent.note && <p className={styles.settleNote}>{intent.note}</p>}

                        {allIssues.length > 0 && (
                            <div className={styles.issues}>
                                {allIssues.map((issue, i) => <div key={i} className={styles.issueItem}>{issue}</div>)}
                            </div>
                        )}
                    </div>
                )}
                {!loading && head && phase === "confirm" && pendingIntent && (
                    <div className={styles.container}>
                        {pendingIntent.hasIssues && (
                            <div className={styles.issues}>
                                <div style={{ fontWeight: 600, marginBottom: "var(--space-1)" }}>Issues detected:</div>
                                {pendingIntent.issues.map((issue, i) => <div key={i} className={styles.issueItem}>{issue}</div>)}
                                <label style={{ display: "flex", alignItems: "center", gap: "var(--space-2)", marginTop: "var(--space-2)" }}>
                                    <input type="checkbox" checked={issuesAcked} onChange={e => setIssuesAcked(e.target.checked)} />
                                    <span>I acknowledge these issues</span>
                                </label>
                            </div>
                        )}
                        <p style={{ whiteSpace: "pre-wrap", margin: "var(--space-2) 0" }}>{pendingIntent.description}</p>
                        {pendingIntent.settleNote && <p className={styles.settleNote}>{pendingIntent.settleNote}</p>}
                        {pendingIntent.subsidyAdjustments.map(a => {
                            const deal = packageDeals.find(d => d.id === a.subsidyId);
                            return (
                                <p key={a.subsidyId} className={styles.confirmNote}>
                                    {`Subsidy ${deal ? `(${deal.id}) ${deal.name}` : `#${a.subsidyId}`} adjustment ${fmtAmt(a.delta)} — written as a new SUBSIDY payment row.`}
                                </p>
                            );
                        })}
                        {pendingIntent.action === "refund" && (
                            <RefundSourcesPanel
                                sources={pendingIntent.refundSources}
                                plan={refundPlan}
                                amount={pendingIntent.refundAmount}
                                onChange={setRefundPlan}
                            />
                        )}
                        <label style={{ display: "block", marginTop: "var(--space-2)" }}>
                            {isTremaSport
                                ? "Comment (optional — left blank logs as “Reason omitted”):"
                                : "Comment (required, > 10 characters):"}
                            <textarea
                                className={styles.commentInput}
                                rows={3}
                                style={{ width: "100%", marginTop: "var(--space-1)" }}
                                value={comment}
                                onChange={e => setComment(e.target.value)}
                                autoFocus
                            />
                            {!isTremaSport && !commentValid && comment.length > 0 && (
                                <span style={{ color: "var(--color-warning)", fontSize: "var(--text-xs)" }}>
                                    {`${11 - comment.trim().length} more character${11 - comment.trim().length === 1 ? "" : "s"} required`}
                                </span>
                            )}
                        </label>
                    </div>
                )}
            </>
        </GridEditModal>
    );
}
